import type { AuthUser } from '../types/auth';

export const CHANGE_PASSWORD_PATH = '/auth/change-password';
export const MFA_ENROLL_PATH = '/auth/mfa-enroll';
export const PRIVACY_NOTICE_PATH = '/auth/privacy-notice';
export const TERMS_PATH = '/auth/terms';

function onLegalPage(pathname: string): boolean {
  return pathname === PRIVACY_NOTICE_PATH || pathname === TERMS_PATH;
}

/**
 * Where an account that still owes something must go instead of the page it asked for, or null.
 * The order is the contract: a password is rotated first, a second factor is enrolled after a
 * settled password, and a client's acceptance of the privacy notice (#289) and of the Terms and Conditions
 * (0181) in force comes last. Each obligation pins the person to its own page only; the two
 * documents share one page that shows whichever is owed, so owing both never sends the person
 * from one to the other.
 */
export function pendingObligationPath(user: AuthUser | null, pathname: string): string | null {
  if (user?.must_change_password === true && pathname !== CHANGE_PASSWORD_PATH) {
    return CHANGE_PASSWORD_PATH;
  }
  if (user?.must_enroll_mfa === true && pathname !== MFA_ENROLL_PATH) {
    return MFA_ENROLL_PATH;
  }
  // Last, and only once nothing else is owed: two pages that each send the person to the other
  // would never settle.
  const nothingElseOwed = user?.must_change_password !== true && user?.must_enroll_mfa !== true;
  if (nothingElseOwed && user?.privacy_notice_owed === true && !onLegalPage(pathname)) {
    return PRIVACY_NOTICE_PATH;
  }
  if (nothingElseOwed && user?.terms_owed === true && !onLegalPage(pathname)) {
    return TERMS_PATH;
  }
  return null;
}
